import type React from 'react';
import { useMemo } from 'react';
import type { GroupBase, SingleValue } from 'react-select';

import { Select as UiSelect } from '@revelio/ui';

import { useRicsTaxonomyLabels } from '../../shared/selection-lists/hooks/useRicsTaxonomyLabels';
import styles from './base-taxonomy-id-select.module.css';

type TaxonomyOption = { value: number | 'adaptive'; label: string };

type BaseTaxonomyCommonProps = {
  isDisabled?: boolean;
  includeCustom?: boolean;
  currentResolvedAdaptiveLabel?: string;
  variant?: 'default' | 'compact';
  usePortal?: boolean;
  customTaxonomies?: Array<{ id: number; label: string }>;
  taxonomyIds?: number[];
  selectedLabelPrefix?: string; // When provided, shows "<prefix>: <label>" in the value area
};

type BaseTaxonomyPropsWithAdaptive = BaseTaxonomyCommonProps & {
  includeAdaptive: true;
  value?: number | 'adaptive';
  onChange: (taxonomyId: number | 'adaptive') => void;
};

type BaseTaxonomyPropsWithoutAdaptive = BaseTaxonomyCommonProps & {
  includeAdaptive?: false | undefined;
  value?: number;
  onChange: (taxonomyId: number) => void;
};

export type BaseTaxonomyIdSelectProps =
  | BaseTaxonomyPropsWithAdaptive
  | BaseTaxonomyPropsWithoutAdaptive;

export const BaseTaxonomyIdSelect: React.FC<BaseTaxonomyIdSelectProps> = (
  props
) => {
  const {
    value,
    isDisabled = false,
    currentResolvedAdaptiveLabel,
    includeCustom = false,
    variant = 'default',
    usePortal = false,
    customTaxonomies = [],
    taxonomyIds,
    selectedLabelPrefix,
  } = props;
  const { labels } = useRicsTaxonomyLabels();

  const options = useMemo(() => {
    type OptionType = TaxonomyOption;
    type OptionGroup = { label: string; options: OptionType[] };

    const groups: OptionGroup[] = [];

    const topOptions: OptionType[] = [];
    if (props.includeAdaptive) {
      const adaptiveLabel = currentResolvedAdaptiveLabel
        ? `Adaptive (${currentResolvedAdaptiveLabel})`
        : 'Adaptive';
      topOptions.push({ value: 'adaptive', label: adaptiveLabel });
    }
    topOptions.push({ value: 0, label: 'Generic' });
    groups.push({ label: '__top__', options: topOptions });

    if (customTaxonomies.length > 0 && includeCustom) {
      const customOptions: OptionType[] = customTaxonomies.map((t) => ({
        value: t.id,
        label: t.label,
      }));
      groups.push({ label: 'Custom Taxonomies', options: customOptions });
    }

    const k50Options: OptionType[] = [];
    if (labels?.rics_k50) {
      labels.rics_k50.forEach((label, id) => {
        if (id >= 1 && id <= 50) {
          if (taxonomyIds && !taxonomyIds.includes(id)) return;
          k50Options.push({ value: id, label: label || 'Unknown' });
        }
      });
      k50Options.sort((a, b) => (a.value as number) - (b.value as number));
    }
    if (k50Options.length > 0) {
      groups.push({ label: 'Industry K50', options: k50Options });
    }

    const k10Options: OptionType[] = [];
    if (labels?.rics_k10) {
      labels.rics_k10.forEach((label, baseId) => {
        const id = baseId + 50;
        if (id >= 51 && id <= 60) {
          if (taxonomyIds && !taxonomyIds.includes(id)) return;
          k10Options.push({ value: id, label: label || 'Unknown' });
        }
      });
      k10Options.sort((a, b) => (a.value as number) - (b.value as number));
    }
    if (k10Options.length > 0) {
      groups.push({ label: 'Industry K10', options: k10Options });
    }

    return groups as unknown as GroupBase<OptionType>[];
  }, [
    labels,
    props.includeAdaptive,
    currentResolvedAdaptiveLabel,
    includeCustom,
    customTaxonomies,
    taxonomyIds,
  ]);

  const selectedOption = useMemo(() => {
    const flat = (options || []).flatMap((g) => g.options);
    if (value === 0) return flat.find((o) => o.value === 0) ?? null;
    if (props.includeAdaptive && value === 'adaptive') {
      return flat.find((o) => o.value === 'adaptive') ?? null;
    }
    if (typeof value === 'number') {
      return flat.find((o) => o.value === value) ?? null;
    }
    return flat.find((o) => o.value === 0) ?? null;
  }, [options, value, props.includeAdaptive]);

  const formatOptionLabel = (
    opt: TaxonomyOption,
    meta: { context: 'menu' | 'value' }
  ) => {
    if (meta.context === 'menu') {
      return opt.label;
    }
    if (selectedLabelPrefix) {
      return `${selectedLabelPrefix}: ${opt.label}`;
    }
    return opt.label;
  };

  const handleChange = (selected: SingleValue<TaxonomyOption>) => {
    if (!selected) return;
    if (props.includeAdaptive && selected.value === 'adaptive') {
      return props.onChange('adaptive');
    }
    return props.onChange(Number(selected.value));
  };

  return (
    <UiSelect<TaxonomyOption, false, GroupBase<TaxonomyOption>>
      isDisabled={isDisabled || !labels}
      isLoading={!labels}
      options={options}
      closeMenuOnScroll={false}
      blurInputOnSelect={false}
      menuShouldScrollIntoView={false}
      backspaceRemovesValue={false}
      isClearable={false}
      menuPortalTarget={
        usePortal && typeof document !== 'undefined' ? document.body : null
      }
      menuPosition={usePortal ? 'fixed' : 'absolute'}
      size={variant === 'compact' ? 'sm' : 'md'}
      formatOptionLabel={formatOptionLabel}
      formatGroupLabel={(group) =>
        group.label === '__top__' ? null : (
          <span className={styles.groupLabel}>{group.label}</span>
        )
      }
      value={selectedOption}
      onChange={handleChange}
    />
  );
};
